import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { submitJobApplications } from "../../../ApiServices/ApiServices";
import { handleError, handleSuccess } from "../../../Utils";

const JobForm = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [resume, setResume] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false); // Loading state

  const location = useLocation();
  const { jobId, jobtitle } = location.state || {};
  const navigateTo = useNavigate();

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError("File size must be less than 5MB!");
        setResume(null);
      } else {
        setResume(file);
        setError("");
      }
    }
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();

    if (!resume) {
      setError("Please upload your resume!");
      return;
    }

    setLoading(true); // Start loading
    const formData = new FormData();
    formData.append("firstName", firstName);
    formData.append("lastName", lastName);
    formData.append("email", email);
    formData.append("phone", phone);
    formData.append("jobId", jobId);
    formData.append("resume", resume);
    formData.append("jobtitle", jobtitle);
    try {
      const response = await submitJobApplications(formData);
      handleSuccess(response.message);
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setResume(null);
      navigateTo('/jobs')
    } catch (error) {
      return handleError(error?.response?.data?.message);
    } finally {
      setLoading(false); // End loading
    }
  };

  return (
    <div className="max-w-2xl mx-auto mt-16 px-6 py-11 bg-white shadow-lg rounded-lg">
      <h1 className="text-3xl font-semibold text-gray-800 mb-8 text-center">
        Candidate's Personal Information
      </h1>

      <form onSubmit={handleFormSubmit}>
        {/* First Name and Last Name */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              onChange={(e) => setFirstName(e.target.value)}
              value={firstName}
              type="text"
              id="firstName"
              name="firstName"
              placeholder="Candidate's First Name"
              className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              required
            />
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              onChange={(e) => setLastName(e.target.value)}
              value={lastName}
              type="text"
              id="lastName"
              name="lastName"
              placeholder="Candidate's Last Name"
              className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              required
            />
          </div>
        </div>

        {/* Email */}
        <div className="mb-6">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            onChange={(e) => setEmail(e.target.value)}
            value={email}
            type="email"
            id="email"
            name="email"
            placeholder="Email"
            className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            required
          />
        </div>

        {/* Phone */}
        <div className="mb-6">
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <select
              id="countryCode"
              name="countryCode"
              className="block w-20 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="+91">IN +91</option>
            </select>
            <input
              onChange={(e) => setPhone(e.target.value)}
              value={phone}
              type="text"
              id="phone"
              name="phone"
              placeholder="Phone Number"
              className="flex-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              required
            />
          </div>
        </div>

        {/* Upload Resume */}
        <div className="mb-6">
          <label htmlFor="resume" className="block text-sm font-medium text-gray-700">
            Upload Resume <span className="text-red-500">*</span>
          </label>
          <div className="mt-2 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-500 transition-all duration-300">
            <input
              type="file"
              id="resume"
              name="resume"
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={handleFileChange}
            />
            <label
              htmlFor="resume"
              className="cursor-pointer flex flex-col items-center justify-center text-gray-500 hover:text-blue-500 transition-all duration-300"
            >
              <img
                src="https://th.bing.com/th/id/OIP.QeJMz5HWVQho3hV8JEQqvAHaHa?rs=1&pid=ImgDetMain"
                alt="Resume Upload Icon"
                className="w-20 h-20 object-contain mb-4"
              />
              {resume ? (
                <p className="text-sm text-green-500">{resume.name}</p>
              ) : (
                <p className="text-sm text-gray-500">
                  Click to upload or drop file (size &lt; 5MB)
                </p>
              )}
            </label>
          </div>
          {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
        </div>

        {/* Submit Button */}
        <div className="mt-6">
          <button
            type="submit"
            className="w-full bg-blue-500 text-white py-3 px-4 rounded-md shadow-lg hover:bg-blue-600 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300"
            disabled={loading} // Disable button during loading
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="w-6 h-6 border-4 border-t-4 border-gray-200 border-solid rounded-full animate-spin"></div>
                <span className="ml-2 text-white">Applying...</span>
              </div>
            ) : (
              "Apply "
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default JobForm;
